import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar } from '@mui/material';

// project-imports
import Drawer from './Drawer';
import Header from './Header';
import HorizontalBar from './Drawer/HorizontalBar';
import Breadcrumbs from 'components/@extended/Breadcrumbs';

import { DRAWER_WIDTH, HIDE_BREADCRUMBS_PAGES } from 'config';
import { getMenuItems } from 'menu-items';
import useConfig from 'hooks/useConfig';
import { dispatch, RootState } from 'store';
import { openDrawer } from 'store/reducers/menu';

// types
import { MenuOrientation } from 'types/config';
import SessionTimer from 'sections/auth/SessionTimer';
import { useEnabledMenuIds } from 'utils/menu/accessController';
import { useSelector } from 'react-redux';
import ScrollTop from 'components/ScrollTop';
import { filter } from 'lodash';
import PageBlocker from 'routes/PageBlocker';
// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const enabledMenuIds = useEnabledMenuIds();
  const location = useLocation();

  const menuList = useMemo(() => getMenuItems(enabledMenuIds), [enabledMenuIds]);

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const { showMenuBar } = useSelector((state: RootState) => state.menu);

  const { pathname: locationPathname } = useLocation();

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      dispatch(openDrawer(!downXL));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  const showBreadCrumbs: boolean = useMemo(
    () => filter(HIDE_BREADCRUMBS_PAGES, (path) => locationPathname.includes(path)).length === 0,
    [locationPathname]
  );

  return (
    <ScrollTop>
      <>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Header />
          {showMenuBar && <>{!isHorizontal ? <Drawer /> : <HorizontalBar />}</>}

          <Box component="main" className="Main" sx={{ width: `calc(100% - ${DRAWER_WIDTH}px)`, flexGrow: 1, p: { xs: 0, md: 2 } }}>
            {downLG && <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit', mb: isHorizontal ? 2 : 'inherit' }} />}
            <Container
              className="MainContainer"
              maxWidth={container ? 'xl' : false}
              sx={{
                xs: 0,
                p: { xs: 1, md: 2 },
                ...(container && { px: { xs: 0, md: 2 } }),
                position: 'relative',
                minHeight: 'calc(100vh - 110px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {showBreadCrumbs ? (
                location.pathname.includes(`design/document-configuration`) ? (
                  <></>
                ) : (
                  <Breadcrumbs navigation={menuList} title card={false} divider={false} />
                )
              ) : (
                <></>
              )}
              <PageBlocker />
              <Outlet />
            </Container>
            <SessionTimer />
          </Box>
        </Box>
      </>
    </ScrollTop>
  );
};

export default MainLayout;
